import * as React from "react";
import { Button, Text, tokens } from "@fluentui/react-components";
import { DismissRegular, CheckmarkCircleFilled } from "@fluentui/react-icons";
import SearchIcon from "@mui/icons-material/Search";
import { useFilterStyles } from "../styles/filterStyles";
import SpreetailHeader from "../layout/SpreetailHeader";

interface Props {
  asinSkuValue: string;
  setAsinSkuValue: (v: string) => void;
  startDate: Date | null;
  endDate: Date | null;
  setStartDate: (d: Date | null) => void;
  setEndDate: (d: Date | null) => void;
  hasDateRange: boolean;
  onSubmit: () => Promise<void>;
  onCancel: () => void;
  showFilterSuccess: boolean;
  streamOptions: string[];
  formatDate: (d: Date | null) => string;
}

export const AsinSkuFilter: React.FC<Props> = ({
  asinSkuValue,
  setAsinSkuValue,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  hasDateRange,
  onSubmit,
  onCancel,
  streamOptions,
  formatDate,
}) => {
  const filterStyles = useFilterStyles();
  const [selectedStream, setSelectedStream] = React.useState("");
  
  return (
    <div className={filterStyles.filterContainer} style={{ height: "100%", minHeight: 0 }}>
      <SpreetailHeader />
      <div className={filterStyles.filterHeader}>
        <Text size={400} weight="semibold" style={{ color: "#0F172A" }}>Filter by ASIN/SKU</Text>
        <Text size={200} style={{ color: "#667085", opacity: 0.9, fontSize: "12px" }}>
          Search and filter your product catalog
        </Text>
      </div>
      
      <div className={filterStyles.filterContent} style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
        {/* ASIN or SKU Field */}
        <div className={filterStyles.inputGroup}>
          <label className={filterStyles.labelText}>
            ASIN or SKU <span className="text-red-500">*</span>
          </label>
          <div className="relative w-full">
            <input
              type="text"
              value={asinSkuValue}
              onChange={(e) => setAsinSkuValue(e.target.value)}
              placeholder="Enter ASIN or SKU"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onSubmit();
                }
              }}
              className="w-full h-12 px-3.5 pr-14 text-sm rounded-xl border-[1.5px] border-gray-200 bg-white/60 placeholder:text-gray-400 focus:outline-none focus:border-2 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] hover:border-gray-200"
              style={{ fontFamily: 'inherit' }}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <SearchIcon className="text-gray-500" style={{ fontSize: '18px' }} />
              </div>
            </div>
          </div>
        </div>
        
        {/* Stream Dropdown */}
        <div className={filterStyles.inputGroup}>
          <label className={filterStyles.labelText}>
            Stream <span className="text-red-500">*</span>
          </label>
          <div className="relative w-full">
            <select
              value={selectedStream}
              onChange={(e) => setSelectedStream(e.target.value)}
              className="w-full h-12 px-3.5 pr-14 text-sm rounded-xl border-[1.5px] border-gray-200 bg-white/60 appearance-none focus:outline-none focus:border-2 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] hover:border-gray-200"
              style={{ 
                fontFamily: 'inherit',
                color: selectedStream ? '#000000' : '#9CA3AF'
              }}
            >
              <option value="" disabled>
                Select Stream
              </option>
              {streamOptions.map((stream) => (
                <option key={stream} value={stream} style={{ color: '#000000' }}>
                  {stream}
                </option>
              ))}
            </select>
            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <SearchIcon className="text-gray-500" style={{ fontSize: '18px' }} />
              </div>
            </div>
          </div>
        </div>
        
        {/* Date Range */}
        <div className={filterStyles.inputGroup}>
          <label className={filterStyles.labelText}>Date Range (Optional)</label>
          <div className={`${filterStyles.dateRangeContainer} flex-nowrap`}>
            <input
              type="date"
              value={startDate ? startDate.toISOString().split('T')[0] : ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  const date = new Date(value);
                  setStartDate(date);
                  if (endDate && date > endDate) {
                    setEndDate(date);
                  }
                } else {
                  setStartDate(null);
                }
              }}
              className="w-full max-w-[45%] min-w-[120px] h-12 px-3.5 text-sm rounded-xl border-[1.5px] border-gray-200 bg-white/60 focus:outline-none focus:border-2 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] hover:border-gray-200"
              style={{ fontFamily: 'inherit' }}
            />
            
            <span className={filterStyles.dateRangeSeparator}>to</span>
            
            <input
              type="date"
              value={endDate ? endDate.toISOString().split('T')[0] : ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  const date = new Date(value);
                  setEndDate(date);
                } else {
                  setEndDate(null);
                }
              }}
              disabled={!startDate}
              className="w-full max-w-[45%] min-w-[120px] h-12 px-3.5 text-sm rounded-xl border-[1.5px] border-gray-200 focus:outline-none focus:border-2 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] hover:border-gray-200 disabled:border-gray-200"
              style={{ 
                fontFamily: 'inherit',
                backgroundColor: startDate ? '#FFFFFF' : '#F9FAFB'
              }}
            />
          </div>
          
          {hasDateRange && (
            <div className={filterStyles.selectedDatesDisplay}>
              <CheckmarkCircleFilled fontSize={12} color={tokens.colorBrandForeground1} />
              <Text size={100} style={{ fontSize: "12px", flex: 1 }}>
                {formatDate(startDate)} — {formatDate(endDate)}
              </Text>
              <Button 
                appearance="transparent" 
                icon={<DismissRegular fontSize={12} />} 
                size="small" 
                onClick={() => { setStartDate(null); setEndDate(null); }} 
                style={{ minWidth: "auto", padding: "2px" }} 
              />
            </div>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className={filterStyles.actionButtons}>
          <Button 
            appearance="outline" 
            size="medium" 
            onClick={onCancel} 
            className={`${filterStyles.actionButton} ${filterStyles.cancelButton}`}
            style={{
              border: '1px solid #0461FE',
              color: '#0461FE'
            }}
          >
            Cancel
          </Button>
          <Button 
            appearance="primary" 
            size="medium" 
            onClick={onSubmit} 
            disabled={false} 
            className={`${filterStyles.actionButton} ${filterStyles.applyButton}`}
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
};